import PageComponent from "components/Page/PageComponent";
import DataListHeader from "components/DataListHeader";
import LoadingSpinner from "components/LoadingSpinner";
import DataTable from "components/DataTable/DataTable";
import { Link } from "@material-ui/core";
import { arrayToObject } from "utils/object-util";
import FilterService, { fieldTypes } from "services/filterService";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useDispatch, useSelector } from "react-redux";
import { setFilters } from "./marginAnalysisSlice";
import { orderBy } from "lodash";
import useSortOptions from "hooks/useSortOptions";
import FormatService from "services/formatService";
import { useGetActivitiesByParamsQuery, useGetAllActivityItemsQuery } from "features/activity/activitySlice";

const MarginAnalysisReport = () => {

    const dispatch = useDispatch();
    const { filters } = useSelector(state => state.marginAnalysisState);
    const [sortOptions, sortData] = useSortOptions();

    let clientId = filters?.clientId?.value;
    let lotId = filters?.lotId?.value;
    let activityDate = filters?.activityDate?.value;

    let { data: activities, isLoading: activitiesLoading } = useGetActivitiesByParamsQuery(
    { 
        clientId: clientId, 
        lotId: lotId,
        startDate: FormatService.formatDate(activityDate?.startDate), 
        endDate: FormatService.formatDate(activityDate?.endDate)}, 
        { skip: !clientId || !lotId || !activityDate?.startDate || !activityDate?.endDate }
    );
    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clients, error: clientError, isLoading: isLoadingClients } = useGetClientsQuery(null);
        
    activities = activities || [];
    activityItems = activityItems || [];
    lots = lots || [];
    clients = clients || [];

    const isLoading = activitiesLoading || isLoadingActivityItems || isLoadingLots || isLoadingClients;

    const activityItemLookup = arrayToObject(activityItems);
    
    let columns = [
        { name: "VIN", key: "descriptor", component: (row) => <Link disabled={!row?.vehicleId} href={`/assets/detail/${row?.vehicleId}`} target="_blank">{row?.descriptor}</Link> },
        { name: "Activity", key: "activityItemId", component: (row) => activityItemLookup[row?.activityItemId]?.name || "Unknown" },
        { name: "Completed Date", key: "activityDate", component: (row) => FormatService.formatDate(row?.activityDate) },
        { name: "Cost", key: "price", component: (row) => row?.price == 0 ? '-' : FormatService.formatCurrency(row?.price) },
        { name: "Charge", key: "revenue", component: (row) => row?.revenue == 0 ? '-' : FormatService.formatCurrency(row?.revenue) },
        { name: "Margin", key: "margin", component: (row) => !row?.price || !row?.revenue ? '-' : `${row?.margin.toFixed(2)}%` },
    ];

    const filterConfig = [
        { type: fieldTypes.TEXT, size: 2, title: 'Client', key: 'clientId', values: clients },
        { type: fieldTypes.LOT, size: 2, title: 'Lot', key: 'lotId', values: lots },
        { type: fieldTypes.DATERANGE, size: 2, title: 'Time Frame', key: 'activityDate', values: [] }
    ];

    let filteredData = FilterService.filter(filters, '', activities, filterConfig);
    
    const onDropFilter = (value, prop) => {
        dispatch(setFilters({ ...filters, [prop]: { value } }));
    };

    const mapExportData = (activity) => {
        return {
            ['VIN']: activity?.descriptor,
            ['Activity']: activityItemLookup[activity.activityItemId]?.name || "Unknown",
            ['Completed Date']: FormatService.formatDate(activity.activityDate),
            ['Cost']: activity?.price == 0 ? '-' : FormatService.formatCurrency(activity.price),
            ['Charge']: activity?.revenue == 0 ? '-' : FormatService.formatCurrency(activity.revenue),
            ['Margin']: !activity?.price || !activity?.revenue ? '-' : `${activity?.margin.toFixed(2)}%`,
        };
    }

    return (
        <PageComponent
            header={<DataListHeader 
                title='Margin Analysis Report'
                data={filteredData || []}
                dropDownsConfig={filterConfig}
                filters={filters}
                onDropFilter={onDropFilter}
            />}
        >
            <>
                {isLoading ? <LoadingSpinner loading={isLoading} /> :
                    <DataTable
                        columns={columns}
                        rowIdentifier='id'
                        rows={orderBy(
                            filteredData,
                            sortOptions.columnToSort,
                            sortOptions.sortDirection
                        )}
                        onSort={sortData}
                        sortDirection={sortOptions.sortDirection}
                        columnToSort={sortOptions.columnToSort}
                        noItemsMessage={!clientId || !lotId || !activityDate ? 'Please select filters to load activities' : 'There are no activities matching this criteria'}
                        mapExportData={mapExportData}
                    />
                }            
            </>
        </PageComponent>
    );
}

export default MarginAnalysisReport;